"use client";

import { Wallet, TrendingDown, Clock, PiggyBank, Droplets, AlertTriangle } from "lucide-react";

interface FinancialDashboardProps {
    currency: string;
    dailySaved: number;
    dailySpent: number;
    dailyLifeRegained: number; // in minutes
    totalSaved: number;
    totalSpent: number;
    yearlyLoss: number;
    decadeLoss: number;
}

export function FinancialDashboard({
    currency,
    dailySaved,
    dailySpent,
    dailyLifeRegained,
    totalSaved,
    totalSpent,
    yearlyLoss,
    decadeLoss,
}: FinancialDashboardProps) {


    const formatMoney = (amount: number) => {
        return amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const formatLife = (minutes: number) => {
        if (minutes < 60) return `${minutes} นาที`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours} ชม. ${mins > 0 ? `${mins} นาที` : ""}`;
    };

    return (
        <div className="space-y-4 animate-in slide-in-from-bottom duration-700">

            {/* 1. Daily Analytics Section */}
            <section className="bg-[#0f172a]/60 backdrop-blur-md rounded-2xl p-5 border border-white/5 shadow-xl">
                <h3 className="text-xs font-bold text-[#94a3b8] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Clock size={14} className="text-[#60a5fa]" />
                    สรุปผลประจำวัน
                </h3>

                <div className="grid grid-cols-2 gap-4">
                    {/* Money Saved */}
                    <div className="bg-[#10b981]/10 rounded-xl p-3 border border-[#10b981]/20">
                        <div className="flex items-center gap-2 mb-1">
                            <PiggyBank size={14} className="text-[#34d399]" />
                            <span className="text-[10px] text-[#a7f3d0]/70 font-medium">ประหยัดได้</span>
                        </div>
                        <div className="text-xl font-black text-[#34d399]">
                            {formatMoney(dailySaved)} <span className="text-[10px] font-normal">{currency}</span>
                        </div>
                    </div>

                    {/* Life Regained */}
                    <div className="bg-[#3b82f6]/10 rounded-xl p-3 border border-[#3b82f6]/20">
                        <div className="flex items-center gap-2 mb-1">
                            <Droplets size={14} className="text-[#60a5fa]" />
                            <span className="text-[10px] text-[#bfdbfe]/70 font-medium">อายุขัยที่ได้คืน</span>
                        </div>
                        <div className="text-lg font-black text-[#60a5fa] truncate">
                            {formatLife(dailyLifeRegained)}
                        </div>
                    </div>

                    {/* Money Spent (Full width if needed, or keeping it distinct) */}
                    <div className="col-span-2 bg-[#ef4444]/5 rounded-xl p-3 border border-[#ef4444]/10 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <TrendingDown size={14} className="text-[#f87171]" />
                            <span className="text-[10px] text-[#fecaca]/70 font-medium">จ่ายไปวันนี้</span>
                        </div>
                        <div className="text-lg font-black text-[#f87171]">
                            {formatMoney(dailySpent)} <span className="text-[10px] font-normal">{currency}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. Cumulative Analytics */}
            <section className="bg-gradient-to-br from-[#312e81]/40 to-[#0f172a]/40 backdrop-blur-md rounded-2xl p-5 border border-[#6366f1]/20 shadow-xl relative overflow-hidden">
                {/* Decorative background */}
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                    <Wallet size={120} className="text-white transform rotate-12" />
                </div>

                <h3 className="text-xs font-bold text-[#a5b4fc] uppercase tracking-wider mb-4 flex items-center gap-2 relative z-10">
                    <Wallet size={14} />
                    ภาพรวมการเงิน
                </h3>

                <div className="space-y-4 relative z-10">
                    {/* Total Saved Big Stat */}
                    <div className="text-center">
                        <div className="text-[10px] text-[#94a3b8] mb-1">เงินออมสะสมทั้งหมด</div>
                        <div className="text-4xl font-black text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                            {formatMoney(totalSaved)}
                            <span className="text-sm font-medium text-[#64748b] ml-2">{currency}</span>
                        </div>
                    </div>

                    {/* Total Spent */}
                    <div className="bg-[#020617]/30 rounded-lg p-3 flex justify-between items-center border border-white/5">
                        <span className="text-[10px] text-[#94a3b8]">เสียไปทั้งหมด</span>
                        <span className="text-sm font-bold text-[#f87171]">{formatMoney(totalSpent)} {currency}</span>
                    </div>
                </div>
            </section>

            {/* 3. Long Term Forecast (Awareness) */}
            <section className="bg-[#0f172a]/40 backdrop-blur-sm rounded-2xl p-5 border border-white/5 opacity-80 hover:opacity-100 transition-opacity">
                <h3 className="text-xs font-bold text-[#64748b] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <AlertTriangle size={14} className="text-[#f59e0b]" />
                    หากไม่เลิกตอนนี้
                </h3>

                <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#94a3b8] text-xs">เสียเงินใน 1 ปี</span>
                        <span className="text-slate-200 font-bold">{formatMoney(yearlyLoss)} <span className="text-[10px] text-[#64748b]">{currency}</span></span>
                    </div>
                    <div className="w-full h-[1px] bg-white/5"></div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#94a3b8] text-xs">เสียเงินใน 10 ปี</span>
                        <span className="text-[#fbbf24] font-bold">{formatMoney(decadeLoss)} <span className="text-[10px] text-[#64748b]">{currency}</span></span>
                    </div>
                </div>

            </section>



        </div>
    );
}
